// OptionSetRowActions — the option-set row kebab: Edit · Delete, the same pair
// the selection bar offers. Both callbacks are OPTIONAL: the tab passes
// `undefined` when the verb (edit / delete) is missing and the item doesn't
// render — absence means hidden; with neither, the kebab renders nothing at
// all (the CompanyFieldRowActions case). Delete is a danger item behind a
// divider (the ResponseSetRowActions shape). Its own component rather than
// inline in the column's `render` because it owns the dropdown's open state,
// and hooks can't live in a render callback.
//
// `portal` is required: the table scrolls horizontally, so an in-flow panel
// would be clipped by that container. Clicks stopPropagation so a menu click
// stays inside the kebab and never reaches the surrounding row.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';
import type { OptionSet } from '@/api/endpoints/formSetsApi';

interface OptionSetRowActionsProps {
  set: OptionSet;
  /** Absent = hidden. */
  onEdit?: (set: OptionSet) => void;
  /** Absent = hidden. */
  onDelete?: (set: OptionSet) => void;
}

const OptionSetRowActions = ({ set, onEdit, onDelete }: OptionSetRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Close the menu, then run the action.
  const pick = (run: (s: OptionSet) => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    run(set);
  };

  if (!onEdit && !onDelete) return null;

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align='end'
      width={200}
      portal
      trigger={
        <IconButton
          icon='ellipsis-vertical'
          variant='ghost'
          size='sm'
          aria-haspopup='menu'
          aria-expanded={open}
          aria-label={t('data_structure_os_row_actions')}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
        />
      }
    >
      {onEdit && (
        <button type='button' className='app-dropdown__item' role='menuitem' onClick={pick(onEdit)}>
          <Icon name='pencil' size={15} />
          <span className='app-dropdown__item-label'>{t('data_structure_os_action_edit')}</span>
        </button>
      )}
      {onEdit && onDelete && <div className='app-dropdown__divider' role='separator' />}
      {onDelete && (
        <button
          type='button'
          className='app-dropdown__item app-dropdown__item--danger'
          role='menuitem'
          onClick={pick(onDelete)}
        >
          <Icon name='trash-2' size={15} />
          <span className='app-dropdown__item-label'>{t('data_structure_os_action_delete')}</span>
        </button>
      )}
    </Dropdown>
  );
};

export default OptionSetRowActions;
